// How a change is made in a cortex project (ADR 0030): ONE description, rendered for each reader — the MCP's
// server instructions (with the tools of each step), the Claude Code plugin's skill, the AGENTS.md of a project
// repo and the developer guide. The fleet's executors describe the same loop for the fleet's own sessions.

export interface ChangeStep {
  title: string;
  /** The step in words, for any reader. */
  text: string;
  /** The MCP tools of the step (their names follow the api's operationIds, or a hand-written tool's). */
  tools: readonly string[];
}

export const CHANGE_LOOP: readonly ChangeStep[] = [
  {
    title: 'The requirement first',
    text: 'Write or update the requirement (from a source document, or by hand), with a test that asserts a real outcome with known expected values — never only that a page renders or a route answers 200.',
    tools: ['import_requirements', 'patch_requirement', 'add_materials', 'requirement_intake', 'create_test'],
  },
  {
    title: 'Build it',
    text: 'Either file a Board card for the agent fleet and queue it, or make the change yourself.',
    tools: ['create_board_request', 'file_requirement_task', 'move_board_request'],
  },
  {
    title: 'Follow the fleet',
    text: 'While a card runs, read its log and its sessions, and steer it with a comment; the worker reads comments as it works.',
    tools: ['get_board_request', 'list_board_request_log', 'list_card_sessions', 'add_board_comment'],
  },
  {
    title: 'The pull request',
    text: 'Open a pull request. It merges when its checks pass and, where the project requires it, a person approved it.',
    tools: ['open_pull_request', 'get_pull_request'],
  },
  {
    title: 'The merge',
    text: 'Approve and merge only after the person confirmed that one merge; the author of a pull request cannot approve it. A person who manages the project can tell you to merge without waiting for approvals or checks: merge_pull_request with force: true and expectHead (the headSha you showed them), only when they ask in their own words.',
    tools: ['approve_pull_request', 'merge_pull_request'],
  },
];

/** Every tool the loop names (the MCP's tests check that each one exists). */
export const changeLoopTools = (): string[] => [...new Set(CHANGE_LOOP.flatMap((s) => s.tools))];

/** The loop in one paragraph, with each step's tools: the MCP's server instructions. */
export function changeLoopLine(): string {
  return `How a change is made: ${CHANGE_LOOP.map(
    (s, i) => `${i + 1}. ${s.title.toLowerCase()} — ${s.text} (${s.tools.join(', ')})`,
  ).join(' ')}`;
}

/** The loop as a numbered Markdown list, with or without its tools. */
export function changeLoopMarkdown({ tools }: { tools: boolean }): string {
  return CHANGE_LOOP.map(
    (s, i) =>
      `${i + 1}. **${s.title}.** ${s.text}${tools ? ` Tools: ${s.tools.map((t) => `\`${t}\``).join(', ')}.` : ''}`,
  ).join('\n');
}
